import { useEffect, useCallback, Dispatch, SetStateAction, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getSpecialists } from '../../../actions/getSpecialists';
import { selectSpecialists, selectSpecialistsFetching } from '../../../selectors/selectSpecialists';
import { selectPickedSpecialists } from '../../../selectors/selectPickedSpecialist';
import { EAnswerField } from '../../../repositories/lead-realtor-form/entities/question/QuestionSchema';
import { TAnswersState } from '../../FormContainer/types/internal';
import { ISpecialistCatalogListingSchema } from '../../../repositories/demand/entities/specialist_catalog/SpecialistCatalogListingSchema';
import { addSpecialist, removeSpecialist } from '../../../actions/pickedSpecialist';
import { IGeoResultValue } from '../../../actions/submitForm/types';
import { trackMaxRealtors, trackSelectRealtor, trackSendForm, trackUnselectRealtor } from './tracking';

interface IUseRealtorListingParams {
  answerField: EAnswerField;
  setNextStep: (answersCollection: TAnswersState) => void;
  setAnswersState: Dispatch<SetStateAction<TAnswersState>>;
  geo: IGeoResultValue | undefined;
}

export const useRealtorListing = ({ answerField, setNextStep, setAnswersState, geo }: IUseRealtorListingParams) => {
  const dispatch = useDispatch();
  const specialistsList = useSelector(selectSpecialists);
  const isFetching = useSelector(selectSpecialistsFetching);
  const pickedSpecialists = useSelector(selectPickedSpecialists);

  const fetchSpecialists = useCallback(() => {
    if (geo) {
      dispatch(getSpecialists({ geo }));
    }
  }, [dispatch]);

  useEffect(() => {
    fetchSpecialists();
  }, [dispatch, fetchSpecialists]);

  const selectedRealtorsId = useMemo(
    () => Object.keys(pickedSpecialists).map(index => Number(index)),
    [pickedSpecialists],
  );

  const handleSelectRealtor = useCallback(
    (item?: ISpecialistCatalogListingSchema | null) => {
      if (item) {
        dispatch(addSpecialist(item));
        const amountSelectedRealtors = selectedRealtorsId.length + 1;
        trackSelectRealtor({ type: item.id, amountSelectedRealtors });
        trackMaxRealtors({ type: item.id, amountSelectedRealtors });
      }
    },
    [dispatch, selectedRealtorsId],
  );

  const handleUnselectRealtor = useCallback(
    (id: string) => {
      dispatch(removeSpecialist(id));
      const amountSelectedRealtors = selectedRealtorsId.length + 1;
      trackUnselectRealtor({ type: Number(id), amountSelectedRealtors });
    },
    [dispatch, selectedRealtorsId],
  );

  const handleSubmit = () => {
    setAnswersState(prevState => {
      const nextAnswersState: TAnswersState = {
        ...prevState,
        [answerField]: {
          answerField,
          value: selectedRealtorsId,
        },
      };

      setNextStep(nextAnswersState);
      trackSendForm(selectedRealtorsId);

      return nextAnswersState;
    });
  };

  return {
    specialistsList,
    selectedRealtorsId,
    fetchSpecialists,
    isFetching,
    handleSelectRealtor,
    handleUnselectRealtor,
    handleSubmit,
  };
};
